import React, { useMemo } from 'react';
import { CheckCircle, AlertTriangle, Layers, Zap, BarChart3 } from 'lucide-react';
import { validateSeedCoverage, validateSeedQuestionsLocal, getAllSeedQuestions, getSeedQuestionTypeDistribution, SEED_COLLECTIONS, getSeedCollection, TAXONOMY, FAMILIES_BY_MODULE, MODULES, type ModuleId } from '../../../../content';
import { QUESTION_TYPE_META } from '../../../../content/questions/questionTypeRegistry';
import type { QuestionType } from '../../../../content/types';
import { getModuleHexColor } from '../../../../content/moduleColors';
export function SeedStatus() {
  const coverageReport = useMemo(() => validateSeedCoverage(), []);
  const validationReport = useMemo(() => validateSeedQuestionsLocal(), []);
  const typeDistribution = useMemo(() => getSeedQuestionTypeDistribution(), []);
  const coveragePercent = Math.round(coverageReport.coveredFamilies / coverageReport.totalFamilies * 100);
  const sortedTypes = useMemo(() => {
    return Object.entries(typeDistribution).sort((a, b) => b[1] - a[1]);
  }, [typeDistribution]);
  return <div className="space-y-6 sm:space-y-8" data-id="element-7875" data-mp="1np7p7l9c0fqv">
      {/* 1. Module Overzicht */}
      <div data-id="element-7876" data-mp="19x9n3ue1fk47">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-7877" data-mp="19xj976vyp74m">
          <BarChart3 className="w-5 h-5 text-blue-600" data-id="element-7878" data-mp="1ewdv4hp87bsy" />
          Module Overzicht
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7879" data-mp="22lyo7tf3fpfy">
          {MODULES.map(module => {
          const collection = SEED_COLLECTIONS.get(module.id);
          const families = FAMILIES_BY_MODULE.get(module.id) || [];
          const questionCount = collection?.questions.length || 0;
          const familyCount = families.length;
          const moduleFamilyIds = families.map(f => f.familyId);
          const coveredModuleFamilies = new Set(collection?.questions.map(q => q.familyId) || []);
          const validCoveredCount = moduleFamilyIds.filter(id => coveredModuleFamilies.has(id)).length;
          const coverage = familyCount > 0 ? Math.round(validCoveredCount / familyCount * 100) : 0;
          const tiers = collection?.questions.map(q => q.tier).filter(Boolean) || [];
          const minTier = tiers.length > 0 ? Math.min(...tiers) : 0;
          const maxTier = tiers.length > 0 ? Math.max(...tiers) : 0;
          return <div key={module.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" style={{
            borderLeftWidth: '4px',
            borderLeftColor: getModuleColor(module.id)
          }} data-id="element-7880" data-mp="3akw2wfcxjb3b">
                <div className="p-4 flex-1" data-id="element-7881" data-mp="14re4crkik6km">
                  <div className="flex justify-between items-start mb-4" data-id="element-7882" data-mp="14q33p0md7exq">
                    <h4 className="font-bold text-gray-900" data-id="element-7883" data-mp="zu492vag10ov">{module.title}</h4>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-md" data-id="element-7884" data-mp="17c2fgqxbvvnv">
                      {collection?.moduleCode || module.id.substring(0, 3).toUpperCase()}
                    </span>
                  </div>

                  <div className="space-y-2" data-id="element-7885" data-mp="1m29o6exja0bd">
                    <div className="flex justify-between text-sm" data-id="element-7886" data-mp="1m0yk0e06eqjq">
                      <span className="text-gray-500" data-id="element-7887" data-mp="of1ah63rxvjx">Vragen</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-7888" data-mp="tcuebshludh1">
                        {questionCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-7889" data-mp="ye9zdkjs8jzf">
                      <span className="text-gray-500" data-id="element-7890" data-mp="12ek5cx7kraea">Families</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-7891" data-mp="29rvm6z2669hs">
                        {familyCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-7892" data-mp="2dusa95ahaxt4">
                      <span className="text-gray-500" data-id="element-7893" data-mp="2ccafvz808zqy">Tier bereik</span>
                      <span className="font-mono font-medium text-gray-900" data-id="element-7894" data-mp="27dh2s7wmrarb">
                        {minTier > 0 ? `${minTier}-${maxTier}` : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100" data-id="element-7895" data-mp="2hcy5qhkhzajl">
                  <div className="flex justify-between text-xs mb-1.5" data-id="element-7896" data-mp="2v91uhvclqqoo">
                    <span className="text-gray-500 font-medium" data-id="element-7897" data-mp="2qcbflbcja14u">Dekking</span>
                    <span className={`font-bold ${coverage === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-7898" data-mp="35069gv2a2bji">
                      {coverage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-7899" data-mp="3e1xgq4cguqfh">
                    <div className={`h-1.5 rounded-full ${coverage === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                  width: `${coverage}%`
                }} data-id="element-7900" data-mp="3a0btpq67wvrk" />
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-7901" data-mp="3om7rj5gdb9mo">
        {/* 2. Dekkingsstatus */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7902" data-mp="39xbnrklcgirv">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-7903" data-mp="3ens2r94tccgc">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-7904" data-mp="3epwc46lhe3o0">
              <CheckCircle className="w-4 h-4 text-emerald-600" data-id="element-7905" data-mp="30ukcplwdrp0r" />
              Dekkingsstatus
            </h3>
          </div>
          <div className="p-4 sm:p-5" data-id="element-7906" data-mp="2qyr2i964w7m5">
            <div className="mb-6" data-id="element-7907" data-mp="2qzsau5vrmg06">
              <div className="flex justify-between items-end mb-2" data-id="element-7908" data-mp="2r0tpekbzpkmn">
                <span className="text-sm font-medium text-gray-600" data-id="element-7909" data-mp="2vx0xojruz87r">
                  Totale dekking
                </span>
                <span className={`text-2xl font-bold ${coveragePercent === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-7910" data-mp="11j6js83jcpiw">
                  {coveragePercent}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden" data-id="element-7911" data-mp="nqgtvskvz0gi">
                <div className={`h-full transition-all duration-500 ${coveragePercent === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                width: `${coveragePercent}%`
              }} data-id="element-7912" data-mp="1kfxxm20b6cfk" />
              </div>
            </div>

            {coverageReport.missingFamilies.length > 0 ? <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-7913" data-mp="16ij5gpio7qjw">
                <h4 className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-7914" data-mp="1xmrgdkotq0">
                  <AlertTriangle className="w-4 h-4" data-id="element-7915" data-mp="3r52u1jz0e4fa" />
                  Ontbrekende families ({coverageReport.missingFamilies.length})
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-7916" data-mp="3m1k4xrklupol">
                  {coverageReport.missingFamilies.map(id => <div key={id} className="flex items-center justify-between bg-white p-2 rounded border border-amber-100 text-sm" data-id="element-7917" data-mp="3qzbysr4t8lvf">
                      <span className="font-mono text-amber-900" data-id="element-7918" data-mp="3naxpav5s6yk">{id}</span>
                    </div>)}
                </div>
              </div> : <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-id="element-7919" data-mp="dn1tdfvggs08">
                <div className="bg-emerald-100 p-2 rounded-full flex-shrink-0" data-id="element-7920" data-mp="ijsb07mwrvnr">
                  <CheckCircle className="w-5 h-5 text-emerald-600" data-id="element-7921" data-mp="2fe57wg8guqq2" />
                </div>
                <div data-id="element-7922" data-mp="2kbydb71y3gpa">
                  <div className="font-bold text-emerald-900" data-id="element-7923" data-mp="2kd9dkww6m6bn">
                    Alle families gedekt
                  </div>
                  <div className="text-sm text-emerald-700" data-id="element-7924" data-mp="q2jeeciya1rp">
                    Alle {coverageReport.totalFamilies} families uit de
                    taxonomie hebben minimaal één seed vraag.
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* 3. Vraagtypen */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7925" data-mp="75i54fid5fhy">
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-7926" data-mp="26x4wg342vti">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-7927" data-mp="2906lxdmmde2">
              <Layers className="w-4 h-4 text-purple-600" data-id="element-7928" data-mp="g4bzs0c4vvsv" />
              Vraagtypen
            </h3>
          </div>
          <div className="p-4 sm:p-5" data-id="element-7929" data-mp="1rllhoifm6fs2">
            <div className="space-y-3" data-id="element-7930" data-mp="1rkk70f2ifvav">
              {sortedTypes.map(([type, count]) => <div key={type} className="flex items-center justify-between" data-id="element-7931" data-mp="1niyks34364o0">
                  <div className="flex items-center gap-2" data-id="element-7932" data-mp="1v2zrpcpr2b5z">
                    <CheckCircle className="w-4 h-4 text-emerald-500" data-id="element-7933" data-mp="1v5lxp7wzsim3" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-7934" data-mp="203do1xhznwo6">
                      {translateQuestionType(type)}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full" data-id="element-7935" data-mp="1q3mtxwxcbjom">
                    {count}
                  </span>
                </div>)}
              {sortedTypes.length === 0 && <div className="text-sm text-gray-500 text-center py-4" data-id="element-7936" data-mp="2isc0wcl2uj8a">
                  Geen vraagtypen gevonden
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* 4. Validatie */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7937" data-mp="2wt4u8tzn789t">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center" data-id="element-7938" data-mp="31jbgwoalgipf">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-7939" data-mp="2hzy5ocbl17fx">
            <Zap className="w-4 h-4 text-amber-500" data-id="element-7940" data-mp="2m1t1x2enoumu" />
            Validatie Resultaten
          </h3>
          <div className="flex gap-2 text-xs font-medium" data-id="element-7941" data-mp="282jvuvk5zgwl">
            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full" data-id="element-7942" data-mp="2821q98bm3qc0">
              {validationReport.valid} Geldig
            </span>
            {validationReport.invalid > 0 && <span className="px-2.5 py-1 bg-rose-100 text-rose-700 rounded-full" data-id="element-7943" data-mp="1yadvaxfujr5s">
                {validationReport.invalid} Ongeldig
              </span>}
          </div>
        </div>
        <div className="p-4 sm:p-5" data-id="element-7944" data-mp="1p9pho3ud7stf">
          {validationReport.issues.length > 0 ? <div className="space-y-3" data-id="element-7945" data-mp="1ub53vz8a45de">
              {validationReport.issues.map(issue => <div key={issue.id} className="bg-rose-50 border border-rose-200 rounded-lg p-3 sm:p-4" data-id="element-7946" data-mp="1feecod8rdzyv">
                  <div className="font-mono text-sm font-bold text-rose-800 mb-2" data-id="element-7947" data-mp="10q5n5t31txdi">
                    {issue.id}
                  </div>
                  <ul className="list-disc list-inside text-sm text-rose-700 space-y-1" data-id="element-7948" data-mp="wr56oh81n6to">
                    {issue.problems.map((p, i) => <li key={i} data-id="element-7949" data-mp="15qa41k7vcu49" data-mpi={i}>{p}</li>)}
                  </ul>
                </div>)}
            </div> : <div className="text-center py-8" data-id="element-7950" data-mp="1jnyr8ypesdch">
              <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-3" data-id="element-7951" data-mp="1fopwbqimu9fc" />
              <p className="text-gray-600 font-medium" data-id="element-7952" data-mp="1tfl93dw95ao7">
                Alle seed vragen zijn succesvol gevalideerd
              </p>
            </div>}
        </div>
      </div>
    </div>;
}
// Helpers
function getModuleColor(moduleId: ModuleId): string {
  const mod = MODULES.find(m => m.id === moduleId);
  if (!mod) return '#64748b';
  return getModuleHexColor(mod.color);
}
// F-A4 (19 May 2026): use QUESTION_TYPE_META as the SSOT for Dutch labels
// instead of a hand-maintained local map. The old map covered only 14 of
// the 38 registered types — anything outside that set rendered raw English
// keys (e.g. `text_highlight`, `verb_conjugation_flow`) and drifted out of
// sync as new types were added. Audit §19.2.4 / §20.1.
function translateQuestionType(type: string): string {
  const meta = QUESTION_TYPE_META[type as QuestionType];
  return meta?.label ?? type;
}